import { useEffect, useRef, useState } from "react";
import { createDecartClient, models } from "@decartai/sdk";
import "../Components/Styles/camera.scss";
import { decartToken } from "../Services/ApiServices/chatServices";

export default function CameraRecorderDecart() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const processedStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const decartClientRef = useRef(null);
  const decartRealtimeRef = useRef(null);

  const [isDecartReady, setIsDecartReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [error, setError] = useState(null);

  /* ===============================
     INIT DECART (CLIENT TOKEN)
     =============================== */
  useEffect(() => {
    const initDecart = () => {
      decartToken((response) => { // for env variables 
        if (response?.data?.client_token) {
          decartClientRef.current = createDecartClient({
            apiKey: response.data.client_token,
          });
        } else {
          console.log("Token missing:");
        }
        }, (err) => {
          console.log('Error occured in fetching token:', err, err.message || err.error);
        });
    };
    // initDecart();
  }, []);

  const isSecureContext = () => {
    if (typeof window === 'undefined') return false;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHttps = window.location.protocol === 'https:';
    return isLocalhost || isHttps;
  };

  const getSecurityErrorMessage = () => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) return null;
    return 'Camera access requires HTTPS. The application is being served over an insecure connection.';
  };

  const handleErrorCameraError = (err) => {
    console.error("Camera error:", err);
    let errorMessage = err?.message || "Failed to start camera";
    if (err?.name === 'NotReadableError') {
      errorMessage = 'Could not start video source. Camera may be in use by another application or unavailable. Close other apps using the camera and try again.';
    } else if (err?.name === 'NotAllowedError') {
      errorMessage = 'Camera/microphone access denied. Please allow permissions.';
    } else if (err?.name === 'NotFoundError') {
      errorMessage = 'No camera found.';
    } else if (err?.name === 'TypeError' && err?.message?.includes('insecure')) {
      errorMessage = 'Camera access blocked: Application must be served over HTTPS for camera access.';
    }
    setError(errorMessage);
    setIsDecartReady(false);
  };

  /* ===============================
     START CAMERA + DECART
     =============================== */
  const startCamera = async () => {
    if (!isSecureContext()) {
      const securityError = getSecurityErrorMessage();
      if (securityError) {
        console.error('Security error: Camera cannot be accessed over insecure (HTTP) connection');
        setError(securityError);
      }
      return;
    }

    if (!decartClientRef.current) return;

    setError(null);
    setIsDecartReady(false);

    const model = models.realtime("lucy-latest");

    let stream;
    try {
      // ✅ FIRST TRY: video + audio with model constraints
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacing,
          frameRate: model.fps,
          width: model.width,
          height: model.height,
        },
        audio: true,
      });
    } catch (audioError) {
      console.warn("Audio not available, falling back to video-only:", audioError);

      // ✅ FALLBACK: video only with model constraints (matches working VTON implementation)
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacing,
          frameRate: model.fps,
          width: model.width,
          height: model.height,
        },
        audio: false,
      });
    }

    streamRef.current = stream;

    decartRealtimeRef.current =
      await decartClientRef.current.realtime.connect(stream, {
        model,
        initialState: {
          prompt: { text: "Natural cinematic video", enhance: true },
        },
        onRemoteStream: (editedStream) => {
          processedStreamRef.current = editedStream;
          videoRef.current.srcObject = editedStream;
          videoRef.current.play();
          setIsDecartReady(true);
        },
        onError: (e) => {
          console.error("Decart error:", e);
          setError(e?.message || "Connection failed");
          setIsDecartReady(false);
        },
      });
  };

  /* ===============================
     RECORDING
     =============================== */
  const startRecording = () => {
    if (!isDecartReady || !processedStreamRef.current) return;

    const recorder = new MediaRecorder(processedStreamRef.current, {
      mimeType: "video/webm;codecs=vp8,opus",
    });

    mediaRecorderRef.current = recorder;
    const chunks = [];

    recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      console.log("✅ Recorded video:", URL.createObjectURL(blob));
    };

    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  /* ===============================
     CLEANUP
     =============================== */
  const stopCamera = () => {
    if (decartRealtimeRef.current) decartRealtimeRef.current.disconnect();
    if (streamRef.current)
      streamRef.current.getTracks().forEach((t) => t.stop());

    processedStreamRef.current = null;
    setIsDecartReady(false);
    setError(null);
  };

  const switchCamera = async () => {
    stopCamera();
    setCameraFacing((p) => (p === "user" ? "environment" : "user"));
    await startCamera();
  };

  return (
    <div className="camera-wrapper">
      <video ref={videoRef} autoPlay playsInline muted />

      <div className="controls">
        <button onClick={startCamera}>Open Camera</button>
        <button onClick={switchCamera}>Switch</button>

        <button
          disabled={!isDecartReady}
          onClick={isRecording ? stopRecording : startRecording}
        >
          {isRecording ? "Stop Recording" : "Start Recording"}
        </button>

        <button onClick={stopCamera}>Close</button>
      </div>

      {error && <p className="error">{error}</p>}
      {!isDecartReady && !error && <p className="loading">Initializing live AI video…</p>}
    </div>
  );
}